import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * Lazy-load Google Maps Places JS and bind autocomplete to a single input.
 * Falls back to a plain text input if no API key is configured in admin settings
 * — so the apply form never breaks even when the key is missing or quota is hit.
 *
 * Props:
 *  - value, onChange(formatted_address, placeData?) — formatted_address always passed
 *  - placeData = { place_id, lat, lng, formatted_address } when user picks a suggestion
 *  - placeholder, required, dataTestid, className
 */
let _gmapsLoadPromise = null;
function loadGoogleMaps(apiKey) {
    if (window.google?.maps?.places) return Promise.resolve();
    if (_gmapsLoadPromise) return _gmapsLoadPromise;
    _gmapsLoadPromise = new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async&callback=__pwGmapsReady`;
        s.async = true;
        s.defer = true;
        s.onerror = () => reject(new Error("Google Maps script failed to load"));
        window.__pwGmapsReady = () => resolve();
        document.head.appendChild(s);
    });
    return _gmapsLoadPromise;
}

export default function CityAutocomplete({ value, onChange, placeholder = "City", required = false, dataTestid = "city-autocomplete", className = "" }) {
    const inputRef = useRef(null);
    const [keyReady, setKeyReady] = useState(false);
    const [keyMissing, setKeyMissing] = useState(false);

    // Fetch the public Google Maps key from admin settings (one-shot, cached implicitly)
    useEffect(() => {
        let cancelled = false;
        api.get("/settings/google-maps")
            .then(({ data }) => {
                if (cancelled) return;
                const k = (data?.api_key || "").trim();
                if (!k) { setKeyMissing(true); return; }
                loadGoogleMaps(k).then(() => { if (!cancelled) setKeyReady(true); })
                    .catch(() => { if (!cancelled) setKeyMissing(true); });
            })
            .catch(() => setKeyMissing(true));
        return () => { cancelled = true; };
    }, []);

    // Bind Places Autocomplete once the script is ready
    useEffect(() => {
        if (!keyReady || !inputRef.current) return;
        const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
            types: ["(cities)"],
            fields: ["formatted_address", "place_id", "geometry"],
        });
        const listener = ac.addListener("place_changed", () => {
            const p = ac.getPlace();
            if (!p) return;
            const formatted = p.formatted_address || inputRef.current?.value || "";
            const placeData = {
                place_id: p.place_id || null,
                formatted_address: formatted,
                lat: p.geometry?.location?.lat?.() ?? null,
                lng: p.geometry?.location?.lng?.() ?? null,
            };
            onChange(formatted, placeData);
        });
        return () => {
            try { listener.remove(); } catch { /* ignore */ }
            // Detach pac container to avoid memory leaks between unmounts
            const cont = document.querySelector(".pac-container");
            if (cont) cont.style.display = "none";
        };
    }, [keyReady, onChange]);

    return (
        <input
            ref={inputRef}
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={keyMissing ? placeholder + " (city, state)" : placeholder}
            required={required}
            data-testid={dataTestid}
            className={`flex h-11 w-full border border-input bg-background px-3 py-2 text-sm rounded-none focus:outline-none focus:border-ring ${className}`}
            autoComplete="off"
        />
    );
}
